import type {
  DSAttendanceExpectedSummaryItem,
  DSAttendanceExpectedSummaryShift
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import type { DSAttendanceSelfCheck } from '@/Network/Types/Requests/DsAttendanceRequest'

/**
 * Eixo 2A da discovery 2026-09-11 — checklist do emissor antes de enviar a folha.
 *
 * `INCOMPLETE_SHEET` é a rejeição mais comum em PROD (246 casos em 60 dias), seguida de
 * `REDIGITIZE` (51): cada uma custa um ciclo inteiro de ida e volta com a unidade. O checklist
 * mostra o que a escala espera e pede confirmação explícita item por item — sem bloquear:
 * "Enviar assim mesmo" grava a declaração e o auditor a lê antes de abrir a folha.
 *
 * Puro de propósito: o texto é o que o teste verifica, sem montar componente.
 */

/** Chaves do `selfCheck`, na ordem em que o mockup lista os itens. */
export type SelfCheckKey =
  | 'pagesLegible'
  | 'dateOnAllPages'
  | 'allShiftsFilled'
  | 'signaturesAndStamps'

export interface SelfCheckItem {
  key: SelfCheckKey
  label: string
}

export type SelfCheckState = Record<SelfCheckKey, boolean>

/** Nenhum item vem pré-marcado: marcar é a declaração, e um default `true` a falsificaria. */
export function emptySelfCheckState(): SelfCheckState {
  return {
    pagesLegible: false,
    dateOnAllPages: false,
    allShiftsFilled: false,
    signaturesAndStamps: false
  }
}

/**
 * Normaliza o que chega do service. `Array.isArray` e não `?? []`: uma resposta fora do
 * contrato (o envelope `{ referenceDate, schedules }` cru, por exemplo) faria `reduce is not a
 * function` no render do `StepChecklist` e travaria o envio da folha.
 */
function toItems(
  summary: DSAttendanceExpectedSummaryItem[] | null | undefined
): DSAttendanceExpectedSummaryItem[] {
  return Array.isArray(summary) ? summary : []
}

/** Soma dos plantões previstos das escalas selecionadas. */
export function totalExpectedDoctorStations(
  summary: DSAttendanceExpectedSummaryItem[] | null | undefined
): number {
  return toItems(summary).reduce((total, item) => total + (item.expectedDoctorStations ?? 0), 0)
}

/** Q22 — ocupados sem identificação das escalas selecionadas (fora do universo esperado). */
export function totalUnidentifiedCoverage(
  summary: DSAttendanceExpectedSummaryItem[] | null | undefined
): number {
  return toItems(summary).reduce((total, item) => total + (item.unidentifiedCoverageCount ?? 0), 0)
}

/** M13-B — linha do checklist; `null` quando não há nenhum (ou backend anterior à feature). */
export function buildUnidentifiedCoverageNotice(
  summary: DSAttendanceExpectedSummaryItem[] | null | undefined
): string | null {
  const count = totalUnidentifiedCoverage(summary)
  if (count === 0) return null
  return `+${count} ${count === 1 ? 'ocupado' : 'ocupados'} s/ ident. fora do universo esperado`
}

/** Turnos agregados por nome — a folha física mistura escalas, o emissor lê por turno. */
export function expectedShiftBreakdown(
  summary: DSAttendanceExpectedSummaryItem[] | null | undefined
): DSAttendanceExpectedSummaryShift[] {
  const byName = new Map<string, DSAttendanceExpectedSummaryShift>()

  for (const item of toItems(summary)) {
    for (const shift of item.shifts ?? []) {
      const current = byName.get(shift.name)
      if (current) current.expected += shift.expected ?? 0
      else byName.set(shift.name, { ...shift, expected: shift.expected ?? 0 })
    }
  }

  return Array.from(byName.values())
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`
}

/**
 * Linha de resumo do card: "Escala prevista: 8 plantões (4 Diurno, 4 Noturno). Páginas
 * enviadas: 4." Sem o `expected-summary` (falha ou backend sem a rota) devolve `null` e o
 * checklist aparece sem a linha — o envio nunca fica preso numa consulta auxiliar.
 */
export function buildExpectedSummaryText(params: {
  summary: DSAttendanceExpectedSummaryItem[] | null | undefined
  pages: number
}): string | null {
  if (toItems(params.summary).length === 0) return null

  const expected = totalExpectedDoctorStations(params.summary)
  const shifts = expectedShiftBreakdown(params.summary).filter((shift) => shift.expected > 0)
  const byShift = shifts.length
    ? ` (${shifts.map((shift) => `${shift.expected} ${shift.name}`).join(', ')})`
    : ''

  return (
    `Escala prevista: ${pluralize(expected, 'plantão', 'plantões')}${byShift}. ` +
    `Páginas enviadas: ${params.pages}.`
  )
}

/**
 * Os 4 itens do mockup. Data e quantidade de plantonistas entram no texto porque é o que o
 * emissor tem de conferir na folha que está na mão dele — um rótulo genérico não confere nada.
 */
export function buildSelfCheckItems(params: {
  formattedDate: string
  expectedDoctorStations: number
}): SelfCheckItem[] {
  const shiftWorkers = params.expectedDoctorStations
  const filledLabel = shiftWorkers
    ? `Entrada e saída preenchidas para os ${shiftWorkers} plantonistas`
    : 'Entrada e saída preenchidas para todos os plantonistas'

  return [
    { key: 'pagesLegible', label: 'Todas as páginas estão legíveis e na orientação certa' },
    {
      key: 'dateOnAllPages',
      label: params.formattedDate
        ? `A data ${params.formattedDate} aparece em todas as páginas`
        : 'A data aparece em todas as páginas'
    },
    { key: 'allShiftsFilled', label: filledLabel },
    { key: 'signaturesAndStamps', label: 'Assinatura e carimbo em todas as linhas' }
  ]
}

/**
 * Payload do campo `selfCheck` do multipart. Item desmarcado vai como `false`: é exatamente a
 * informação que o auditor precisa ("foi confessado antes de abrir a folha").
 */
export function buildSelfCheckPayload(
  state: SelfCheckState,
  notes?: string | null
): DSAttendanceSelfCheck {
  const payload: DSAttendanceSelfCheck = {
    pagesLegible: state.pagesLegible === true,
    dateOnAllPages: state.dateOnAllPages === true,
    allShiftsFilled: state.allShiftsFilled === true,
    signaturesAndStamps: state.signaturesAndStamps === true
  }
  const trimmed = notes?.trim()
  if (trimmed) payload.notes = trimmed
  return payload
}
